// src/pages/RequestAccess.js
import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import BusinessInfoForm from '../components/forms/BusinessInfoForm';
import { useAccessRequest } from '../hooks/useAccessRequest';
import { clearAllCache } from '../utils/cacheUtils';

const RequestAccess = () => {
  const { user, logout } = useAuth();
  const { 
    isSubmitting, 
    isSubmitted, 
    isChecking, 
    error, 
    hasExistingRequest,
    submitRequest, 
    checkExisting,
    clearError 
  } = useAccessRequest();

  // Check for existing request on component mount
  useEffect(() => {
    checkExisting();
  }, [checkExisting]);

  const handleSubmit = async (businessData) => {
    await submitRequest(businessData);
  };

  const handleLogout = async () => {
    try {
      clearAllCache();
      await logout();
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.reload();
    }
  };

  // If checking for existing request, show loading
  if (isChecking) {
    return (
<div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="animate-spin w-8 h-8 text-purple-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-gray-600">בודק בקשות קיימות...</p>
        </div>
      </div>
    );
  }

  // If user already has a pending request
  if (hasExistingRequest) {
    return (
<div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            בקשה ממתינה לאישור
          </h2>
          
          <p className="text-gray-600 mb-8 leading-relaxed">
            כבר שלחת בקשת גישה למערכת.
            <br />
            הבקשה ממתינה לבדיקה ואישור של מנהל המערכת.
          </p>
          
          <button
            onClick={handleLogout}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105"
          >
            חזור להתחברות
          </button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
<div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            בקשה נשלחה בהצלחה
          </h2>
          
          <p className="text-gray-600 mb-8 leading-relaxed">
            הבקשה שלך לגישה למערכת התקבלה.
            <br />
            מנהל המערכת יבדוק את הבקשה ויחזור אליך בהקדם.
          </p>
          
          <button
            onClick={handleLogout}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105"
          >
            חזור להתחברות
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 p-4 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <img
                  src="/clinic-q-logo.png"
                  alt="BQS Logo"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            בקש גישה למערכת
          </h1>
          
          <p className="text-gray-600">
            שלום {user?.displayName || user?.email}
            <br />
            מלא את הפרטים כדי לבקש גישה למערכת ניהול העסק
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700 transition-colors duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Business Info Form */}
        <BusinessInfoForm 
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          userEmail={user?.email}
          userName={user?.displayName}
        />

        {/* Logout Option */}
        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <button
            onClick={handleLogout}
            className="text-gray-500 hover:text-gray-700 text-sm transition-colors duration-200"
          >
            התנתק וחזור להתחברות
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestAccess;